import type { EstimateStatus } from './contractsMirror.js';

/**
 * Typed search params for the `/estimates` route. The status filter + the
 * archived toggle live in the URL so they are deep-linkable AND compile-checked
 * (the reports/invoices `searchParams` pattern). TanStack Router validates the
 * raw URL params through `validateEstimatesSearch` on every navigation.
 *
 * `?status=<EstimateStatus>` filters the list to one status (the status-filter
 * pills NAVIGATE with this — the reports `RangeFilter` pattern). `?show_archived=1`
 * flips the list to archived estimates (legacy parity — `EstimatesListPage`
 * `showArchived`). The coarse endpoint (21-05) owns the filtering + the counts;
 * the SPA only carries the flags.
 */

/** The statuses the list-filter pills expose (the active/archived `archived` is the toggle, not a pill). */
const FILTERABLE_STATUSES: readonly EstimateStatus[] = [
  'draft',
  'ready',
  'sent',
  'approved',
  'rejected',
  'converted',
  'expired',
];

export interface EstimatesSearch {
  status?: EstimateStatus;
  show_archived?: boolean;
}

/** Coerce a raw URL value into a boolean (`1`/`true` → true). */
function asBool(value: unknown): boolean {
  return value === true || value === '1' || value === 'true';
}

/** Coerce a raw URL value into a known `EstimateStatus`, else undefined. */
function asStatus(value: unknown): EstimateStatus | undefined {
  return FILTERABLE_STATUSES.find((s) => s === value);
}

/**
 * TanStack Router `validateSearch` for the estimates list route. Keeps the
 * status filter only when it is a known status, and the archived flag only when
 * truthy, so the canonical URL stays clean (`/estimates` vs
 * `/estimates?status=draft` vs `/estimates?show_archived=1`).
 */
export function validateEstimatesSearch(raw: Record<string, unknown>): EstimatesSearch {
  const search: EstimatesSearch = {};
  const status = asStatus(raw.status);
  if (status) search.status = status;
  if (asBool(raw.show_archived)) search.show_archived = true;
  return search;
}
